import { validatePosition, type Point } from '../utils/playground'
import { selectPlaygroundFeatures } from '../playgroundSlice'
import { useReduxSelector } from 'store'
import { useRef } from 'react'
import type React from 'react'

export const SCALE_MIN = 0.099999
export const SCALE_MAX = 1.999999

const useStageMove = (
  scale: number,
  position: Point,
  stageRef: React.RefObject<HTMLDivElement>,
  stagePositionRef: React.RefObject<HTMLDivElement>,
  stageContainerRef: React.RefObject<HTMLDivElement>,
  stageMainRef: React.RefObject<HTMLDivElement>,
  setScale: React.Dispatch<React.SetStateAction<number>>,
  setPosition: React.Dispatch<React.SetStateAction<Point>>
) => {
  const features = useReduxSelector(selectPlaygroundFeatures)
  const offset = useRef(getOffsetOfElementInWindow(stageRef))
  const touchActive = useRef(false)

  const { width = 1, height = 1 } = features || {}

  const handleMouse: React.MouseEventHandler<HTMLDivElement> = (event) => {
    const stagePosition = stagePositionRef.current
    const stage = stageRef.current
    if (!stage || !stagePosition) {
      return
    }

    const extremePos = calculateExtremePosition(stageRef, width * scale, height * scale)
    const { clientX: initX, clientY: initY } = event
    let shiftX = 0
    let shiftY = 0

    const onMouseMove = (e: MouseEvent) => {
      const potentialXPosition = e.clientX - initX
      const potentialYPosition = e.clientY - initY
      shiftX = validatePosition(position.x + potentialXPosition, extremePos.x.min, extremePos.x.max) - position.x
      shiftY = validatePosition(position.y + potentialYPosition, extremePos.y.min, extremePos.y.max) - position.y
      stagePosition.style.translate = `${shiftX}px ${shiftY}px`
    }

    const onClick = (ev: Event) => {
      if (shiftX > 1 || shiftY > 1) ev.stopPropagation()
      event.target.removeEventListener('click', onClick)
    }

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseleave', onMouseUp)
      window.removeEventListener('mouseup', onMouseUp)
      stagePosition.style.removeProperty('translate')
      stage.style.cursor = 'grab'

      if (shiftX || shiftY) {
        setPosition(p => ({
          x: p.x + shiftX,
          y: p.y + shiftY
        }))
      }
    }

    stage.style.cursor = 'grabbing'
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseleave', onMouseUp)
    window.addEventListener('mouseup', onMouseUp)
    event.target.addEventListener('click', onClick)
  }

  const handleTouch: React.TouchEventHandler<HTMLDivElement> = (event) => {
    const currentOffset = getOffsetOfElementInWindow(stageRef)
    const stageContainer = stageContainerRef.current
    const stagePosition = stagePositionRef.current
    const stageMain = stageMainRef.current
    const stage = stageRef.current
    if (touchActive.current || !stage || !stageMain || !stagePosition) {
      return
    }

    touchActive.current = true
    const extremePos = calculateExtremePosition(stageRef, width * scale, height * scale)
    const initX = event.touches[0].clientX
    const initY = event.touches[0].clientY
    let initMidPoint = { x: 0, y: 0 }
    let pinch: boolean | undefined
    let initDistance = 0
    let currentScale = 0
    let shiftX = 0
    let shiftY = 0

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinch === undefined) {
        if (initDistance !== 0) {
          const currentDistance = getDistance(e.touches)
          pinch = initDistance !== currentDistance

        } else {
          initMidPoint = getMidPoint(e.touches, currentOffset.left, currentOffset.top)
          initDistance = getDistance(e.touches)
        }
      }

      if (pinch) {
        const currentDistance = getDistance(e.touches)
        currentScale = scale * (currentDistance / initDistance)
        currentScale = Math.max(SCALE_MIN, Math.min(SCALE_MAX, currentScale))

        const scaleFactor = currentScale / scale
        const currentMidPoint = getMidPoint(e.touches, currentOffset.left, currentOffset.top)
        shiftX = currentMidPoint.x - initMidPoint.x - (initMidPoint.x - position.x) * (scaleFactor - 1)
        shiftY = currentMidPoint.y - initMidPoint.y - (initMidPoint.y - position.y) * (scaleFactor - 1)

        stageMain.style.scale = `${currentScale}`
        stageMain.style.setProperty('--stage-scale', `${currentScale}`)
        stagePosition.style.translate = `${shiftX}px ${shiftY}px`
        if (stageContainer) {
          stageContainer.style.width = `${width * currentScale}px`
          stageContainer.style.height = `${height * currentScale}px`
        }

      } else if (e.touches.length === 1) {
        const potentialXPosition = e.touches[0].clientX - initX
        const potentialYPosition = e.touches[0].clientY - initY
        shiftX = validatePosition(position.x + potentialXPosition, extremePos.x.min, extremePos.x.max) - position.x
        shiftY = validatePosition(position.y + potentialYPosition, extremePos.y.min, extremePos.y.max) - position.y
        stagePosition.style.translate = `${shiftX}px ${shiftY}px`
      }
    }

    const onTouchEnd = () => {
      touchActive.current = false
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchcancel', onTouchEnd)
      window.removeEventListener('touchend', onTouchEnd)
      stagePosition.style.removeProperty('translate')

      if (pinch) {
        setScale(currentScale)
      }

      if (shiftX || shiftY) {
        setPosition(p => ({
          x: p.x + shiftX,
          y: p.y + shiftY
        }))
      }
    }

    window.addEventListener('touchmove', onTouchMove)
    window.addEventListener('touchcancel', onTouchEnd)
    window.addEventListener('touchend', onTouchEnd)
  }

  const onWheel: React.WheelEventHandler<HTMLDivElement> = (event) => {
    const { deltaY, clientX, clientY } = event
    const { left, top } = offset.current
    const newScale = scale * (deltaY > 0 ? 0.97 : 1.03)
    const clamped = Math.max(SCALE_MIN, Math.min(SCALE_MAX, newScale))

    if (clamped === scale) return
    const scaleFactor = clamped / scale
    const cursorX = clientX - left
    const cursorY = clientY - top

    setPosition(p => ({
      x: p.x + (p.x - cursorX) * (scaleFactor - 1),
      y: p.y + (p.y - cursorY) * (scaleFactor - 1)
    }))
    setScale(clamped)
  }

  return {
    handleMouse,
    handleTouch,
    onWheel
  }
}

const getMidPoint = (touches: React.TouchList | TouchList, offsetLeft = 0, offsetTop = 0) => {
  if (touches.length < 2) return { x: 0, y: 0 }
  return {
    x: (touches[0].clientX + touches[1].clientX) / 2 - offsetLeft,
    y: (touches[0].clientY + touches[1].clientY) / 2 - offsetTop
  }
}

const getDistance = (touches: React.TouchList | TouchList) => {
  if (touches.length < 2) return 0
  const dx = touches[0].clientX - touches[1].clientX
  const dy = touches[0].clientY - touches[1].clientY
  return Math.sqrt(dx * dx + dy * dy)
}

const calculateExtremePosition = (ref: React.RefObject<HTMLDivElement>, width: number, height: number) => {
  const containerWidth = ref.current?.clientWidth || window.innerWidth
  const containerHeight = ref.current?.clientHeight || window.innerHeight
  const hw = (width / 2.0)
  const hh = (height / 2.0)

  return {
    x: { min: -hw, max: containerWidth + hw },
    y: { min: -hh, max: containerHeight + hh }
  }
}

const getOffsetOfElementInWindow = (ref: React.RefObject<HTMLDivElement>) => {
  const rect = ref.current?.getBoundingClientRect()
  return {
    left: rect?.left || 0,
    top: rect?.top || 0,
  }
}

export default useStageMove